import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header/header';
import { useConnection } from '../connection/Application';
import { CbaPaperSummary, readList } from '../components/Cba/cbaApi';
import PaperList from '../components/Cba/PaperList';

/**
 * The mock papers in one subject.
 *
 * Each card carries what this student has done with the paper, so the choice of
 * which to sit next is made here rather than after opening one.
 */
const CbaPapers: FC = () => {
  const { categoryId } = useParams();
  const connection = useConnection();
  const navigate = useNavigate();

  const [papers, setPapers] = useState<CbaPaperSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPapers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await connection.get('cba/papers', { categoryId, size: 50 });
      setPapers(readList<CbaPaperSummary>(response).data);
    } catch (e) {
      setPapers([]);
      setError('The exam papers could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [categoryId, connection]);

  useEffect(() => {
    fetchPapers();
  }, [fetchPapers]);

  const subjectName = papers[0]?.subject?.value ?? 'Exam papers';
  const crumbs = [
    { label: 'O/L CBA Exam Papers', href: '/cba' },
    { label: subjectName, href: `/cba/subjects/${categoryId}` },
  ];

  return (
    <Container sx={{ minHeight: 'calc(100vh - 185px)' }}>
      <CssBaseline />
      <Header x={{ crumbs }} />

      <Box sx={{ pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Button onClick={() => navigate(`/cba?category=${categoryId}`)} sx={{ mb: 1, pl: 0 }}>
          &larr; All subjects
        </Button>

        <Typography variant='h4' sx={{ color: '#C44A1E', fontWeight: 500 }}>
          {subjectName}
        </Typography>

        {error !== '' && (
          <Alert severity='error' sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : papers.length === 0 ? (
          <Alert severity='info' sx={{ mt: 3 }}>
            There are no exam papers in this subject yet.
          </Alert>
        ) : (
          <PaperList papers={papers} />
        )}
      </Box>
    </Container>
  );
};

export default CbaPapers;
